import { useEffect, useState, useRef } from "react";
import { Table, Popover, Button, Descriptions, DatePicker, Input, Space } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import HighLighter from "react-highlight-words";
import { getFirestore, getDocs, collection, query, orderBy } from "firebase/firestore";
import { mapDocs, formatTimestamp } from "../../utils";


export const LogsTable = () => {

    const db = getFirestore();

    const { RangePicker } = DatePicker;

    const [logs, setLogs] = useState();
    const [filtered, setFiltered] = useState();
    const [filterValue, setFilterValue] = useState();

    const getLogs = async () => {
        const logs = await getDocs(query(collection(db, "logs"), orderBy("timestamp", "desc")));
        setLogs(mapDocs(logs));
        setFiltered(mapDocs(logs));
    }

    useEffect(() => {
        getLogs();
        // eslint-disable-next-line
    }, []);

    // search input
    const searchInput = useRef(null);

    const handlePagination = () => { }

    const handleFilterChange = (value) => {

        setFilterValue(value);

        if (value === undefined) {
            setFiltered(logs);
            return;
        }

        const filtered = logs.filter((log) => {
            const timestamp = new Date(log.timestamp.seconds * 1000);
            return timestamp >= value[0] && timestamp <= value[1];
        });

        setFiltered(filtered);
    }

    const contentDataOrder = (data) => (
        <Descriptions
            bordered
            size={"small"}
            style={{
                width: 500,
            }}
        >
            <Descriptions.Item label="Id Usuario" span={4}>
                {data.userId}
            </Descriptions.Item>
            <Descriptions.Item label="Detalle" span={4}>
                {data.detail}
            </Descriptions.Item>
            <Descriptions.Item label="Data" span={4}>
                {data.log_data}
            </Descriptions.Item>
            <Descriptions.Item label="Version" span={4}>
                {data.version || "sin version"}
            </Descriptions.Item>
        </Descriptions>
    );

    // search text
    const [search, setSearch] = useState("");
    // search field
    const [searchedCol, setSearchedCol] = useState("");

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearch("");
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearch(selectedKeys[0]);
        setSearchedCol(dataIndex);
    };

    const getColSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            close,
        }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Buscar por ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: "block",
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                        className="btnOk"
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                        className="btnCancel"
                    >
                        Reiniciar
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearch(selectedKeys[0]);
                            setSearchedCol(dataIndex);
                        }}
                        className="text-link"
                    >
                        Filtrar
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                        className="text-link"
                    >
                        Cerrar
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? "#189Off" : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedCol === dataIndex ? (
                <HighLighter
                    highlightStyle={{
                        backgroundColor: "#ffc069",
                        padding: 0,
                    }}
                    searchWords={[search]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });


    const columns = [
        {
            title: "Usuario",
            dataIndex: "user",
            key: "user",
            filters: logs && logs.length ? [...new Map(logs.map(log => [log.user, log])).values()].map((log) => {
                return {
                    text: log.user,
                    value: log.user,
                }
            }).sort((a, b) => a.text.localeCompare(b.text)) : null,
            onFilter: (value, record) => record.user.indexOf(value) === 0,
        },
        {
            title: "Rol",
            dataIndex: "role",
            key: "role",
            filters: logs && logs.length ? [...new Map(logs.map(log => [log.role, log])).values()].map((log) => {
                return {
                    text: log.role,
                    value: log.role,
                }
            }).sort((a, b) => a.text.localeCompare(b.text)) : null,
            onFilter: (value, record) => record.role.indexOf(value) === 0,
        },
        {
            title: "Accion",
            dataIndex: "action",
            key: "action",
            filters: logs && logs.length ? [...new Map(logs.map(log => [log.action, log])).values()].map((log) => {
                return {
                    text: log.action,
                    value: log.action,
                }
            }).sort((a, b) => a.text.localeCompare(b.text)) : null,
            onFilter: (value, record) => record.action.indexOf(value) === 0,
        },
        {
            title: "Pedido",
            dataIndex: "order",
            key: "order",
            ...getColSearchProps("order"),
        },
        {
            title: "Detalle",
            dataIndex: "detail",
            key: "detail",
            render: (_, record) => {
                return (
                    <Popover
                        content={contentDataOrder(record)}
                        title="Detalle"
                        trigger="click"
                    >
                        <Button type="link" className="text-link">Ver mas</Button>
                    </Popover>
                );
            },
        },
        {
            title: "Timestamp",
            dataIndex: "timestamp",
            key: "timestamp",
            render: (_, record) => {
                return (
                    <>
                        {typeof record.timestamp !== "string" ? (
                            <span>{formatTimestamp(record.timestamp, "hour")}</span>
                        ) : null}
                    </>
                );
            },
        }
    ];

    return (
        <>
            {!logs || !logs.length ? (
                <p>Cargando logs ...</p>
            ) : (
                <>
                    <div>
                        <RangePicker
                            showTime
                            onChange={handleFilterChange}
                            value={filterValue}
                        />
                        <Button
                            style={{
                                marginLeft: "20pt",
                            }}
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => getLogs()}
                        >Actualizar</Button>
                    </div>
                    <br /><br />
                    <Table
                        dataSource={filtered}
                        columns={columns}
                        pagination={handlePagination}
                    />
                </>
            )}
        </>
    );
}